import { useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authService } from '@services/authService';

const MICROFRONTEND_URL = (import.meta as any).env?.VITE_CODE_GRAPH_MFE_URL || 'http://localhost:5174';
// postMessage targetOrigin must be scheme+host+port only (no path)
const MICROFRONTEND_ORIGIN = new URL(MICROFRONTEND_URL).origin;

/**
 * CodeGraphWrapper - Iframe wrapper for the Code Graph microfrontend
 *
 * Handles:
 * - Embedding the microfrontend via iframe
 * - postMessage communication for authentication
 * - Navigation events from the microfrontend
 */
export function CodeGraphWrapper() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Strip trailing slash if present to avoid //new or //projectId paths
  const baseUrl = MICROFRONTEND_URL.replace(/\/$/, '');

  // Build iframe URL based on whether we have a projectId
  const iframeSrc = projectId
    ? `${baseUrl}/${projectId}`
    : `${baseUrl}/new`;

  // Send auth token to iframe — always ensure freshness first
  const sendAuthToken = useCallback(async () => {
    try {
      // Refresh if token expires within 60 seconds
      await authService.getKeycloakInstance()?.updateToken(60);
    } catch {
      // Ignore — refresh may fail if session ended; send whatever we have
    }
    const token = authService.getToken();
    console.log('CodeGraphWrapper: Sending auth token to microfrontend:', token ? 'token present' : 'no token');
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'AUTH_TOKEN', token: token || null },
      MICROFRONTEND_ORIGIN
    );
  }, []);

  // Handle messages from iframe
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // Only accept messages from microfrontend origin
      if (event.origin !== MICROFRONTEND_ORIGIN) return;

      const { type, ...data } = event.data || {};

      switch (type) {
        case 'REQUEST_AUTH': {
          // Get auth token from Keycloak service and send it
          sendAuthToken();
          break;
        }

        case 'NAVIGATE': {
          // Navigate to a new path in the main app
          const path = data.path as string;
          if (path) {
            navigate(path);
          }
          break;
        }

        case 'PROJECT_CREATED': {
          // Handle project creation notification
          const newProjectId = data.projectId as string;
          console.log('Project created:', newProjectId);
          break;
        }

        default:
          break;
      }
    },
    [navigate, sendAuthToken]
  );

  // Set up message listener
  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // Push a fresh token to the iframe every 4 minutes so long-running operations
  // (enrichment polling, analysis) never hit a stale-token 401
  useEffect(() => {
    const interval = setInterval(() => {
      if (iframeRef.current) {
        sendAuthToken();
      }
    }, 4 * 60 * 1000);
    return () => clearInterval(interval);
  }, [sendAuthToken]);

  // Handle iframe load - proactively send auth token
  const handleIframeLoad = useCallback(() => {
    console.log('CodeGraphWrapper: Iframe loaded, sending auth token proactively');
    // Small delay to ensure the iframe's message listener is set up
    setTimeout(() => {
      sendAuthToken();
    }, 100);
  }, [sendAuthToken]);

  return (
    <div className="h-[calc(100vh-64px)] w-full -m-8">
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title="Code Graph Viewer"
        className="w-full h-full border-0"
        allow="clipboard-write"
        onLoad={handleIframeLoad}
      />
    </div>
  );
}
